import React, { useState, useEffect, useCallback } from 'react';
import { FiMenu, FiSearch, FiBell, FiUser, FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './Header.css';

const Header = ({ toggleMobileSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Add tipe_anggota for dosen to get sitasi pending notifications
      let url = `/api/notifications.php?user_id=${user.id}&role=user`;
      if (user.role === 'dosen') {
        url += '&tipe_anggota=dosen';
      }
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setNotifications(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/katalog?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return '✓';
      case 'warning': return '⚠';
      case 'error': return '!';
      default: return 'ℹ';
    }
  };

  return (
    <header className="header">
      <div className="header__left">
        <button className="header__mobile-toggle" onClick={toggleMobileSidebar}>
          <FiMenu />
        </button>
        
        <form className="header__search" onSubmit={handleSearch}>
          <FiSearch className="header__search-icon" />
          <input 
            type="text"
            placeholder="Cari buku, jurnal, atau penulis..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="header__search-input"
          />
        </form>
      </div>

      <div className="header__right">
        {/* Notifications */}
        <div className="header__notifications">
          <button 
            className="header__icon-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <FiBell />
            {notifications.length > 0 && (
              <span className="header__notification-badge">{notifications.length}</span>
            )}
          </button>
          
          {showNotifications && (
            <div className="header__dropdown">
              <div className="header__dropdown-header">
                <h4>Notifikasi</h4>
                <button className="header__dropdown-close" onClick={() => setShowNotifications(false)}>
                  <FiX />
                </button>
              </div>
              <div className="header__dropdown-content">
                {loading ? (
                  <p className="header__dropdown-loading">Memuat...</p>
                ) : notifications.length > 0 ? (
                  notifications.map(notif => (
                    <Link 
                      key={notif.id} 
                      to={notif.link || '/peminjaman'}
                      className={`header__notification-item header__notification-item--${notif.type}`}
                      onClick={() => setShowNotifications(false)}
                    >
                      <span className={`header__notification-icon header__notification-icon--${notif.type}`}>
                        {getNotificationIcon(notif.type)}
                      </span>
                      <div className="header__notification-content">
                        <strong className="header__notification-title">{notif.title}</strong>
                        <p className="header__notification-message">{notif.message}</p>
                        {notif.catatan && (
                          <p className="header__notification-note">💬 {notif.catatan}</p>
                        )}
                        <span className="header__notification-time">{notif.time}</span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="header__dropdown-empty">🎉 Tidak ada notifikasi</p>
                )}
              </div>
              {notifications.length > 0 && (
                <div className="header__dropdown-footer">
                  <Link to="/peminjaman" onClick={() => setShowNotifications(false)}>
                    Lihat Semua
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="header__user" onClick={() => navigate('/profil')}>
          <div className="header__user-avatar">
            {user?.foto ? (
              <img src={`${user.foto}`} alt={user.nama} />
            ) : (
              user?.nama?.charAt(0).toUpperCase() || <FiUser />
            )}
          </div>
          <div className="header__user-info">
            <span className="header__user-name">{user?.nama || 'User'}</span>
            <span className="header__user-role">
              {user?.role === 'dosen' ? 'Dosen' : 'Mahasiswa'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
